import { Link, Text, WrapItem } from '@chakra-ui/react'
import { Trans } from '@lingui/react/macro'
import { type FC, type MouseEvent, memo, useCallback } from 'react'

import { getPuzzelApi, isFullMode } from '../../../puzzelApi'
import { logEvents } from '../../logEvents'
import type { ChatLink } from './useAskCopilot'

const openInNewWindow = (link: ChatLink) =>
  window.open(link.url, '_blank', 'noopener,noreferrer')

type KnowledgeBaseLinkProps = {
  link: ChatLink
}

export const KnowledgeBaseLink: FC<KnowledgeBaseLinkProps> = memo(
  ({ link }) => {
    const handleLinkClick = useCallback(
      async (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()

        if (!link.puzzelKbId || !isFullMode()) {
          logEvents({
            eventType: 'kbArticleOpenLinkInNewWindowAttempt',
            link,
          })

          openInNewWindow(link)
          return
        }

        try {
          fetch(link.url, {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store',
          }).catch(() => {
            // used for tracking the clicks, ignore errors
          })

          const api = await getPuzzelApi()
          const tabUidResult = await api.get('tab.uid')
          const relatedTabUid =
            typeof tabUidResult === 'string'
              ? tabUidResult
              : String(tabUidResult || '')

          const articleId = Number(link.puzzelKbId)
          const data = {
            articleId,
            language: link.language,
            tab: {
              focus: true,
              relatedTabUid,
            },
          }

          await api.call('events.publish', 'SYSTEM_KB_ARTICLE_SHOW', {
            data,
          })

          logEvents({
            eventType: 'kbArticleShowPublished',
            link,
            payload: data,
          })
        } catch (_err) {
          logEvents({
            eventType: 'kbArticleShowError',
            link,
            message: _err instanceof Error ? _err.message : String(_err || ''),
          })

          // Graceful fallback if API not available
          openInNewWindow(link)
        }
      },
      [link.id, link.puzzelKbId, link.url, link],
    )

    return (
      <WrapItem>
        <Link
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          backgroundColor="greys.100"
          borderRadius="2xl"
          px={2.5}
          py={1}
          display="flex"
          flexWrap="wrap"
          gap="1"
          fontSize="sm"
          color="greys.600"
          cursor="pointer"
          _hover={{
            textDecoration: 'none',
            backgroundColor: 'greys.50',
          }}
          onClick={handleLinkClick}
        >
          <Text>{link.title}</Text>
          <Text fontWeight="bold" whiteSpace="nowrap">
            <Trans>(match {Math.round(link.score * 100)}%)</Trans>
          </Text>
        </Link>
      </WrapItem>
    )
  },
)
